import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import React, { FunctionComponent, useRef } from "react";
import { Box, Button } from "@mui/material";

/**
 * Interface defining the props for the ExcelFileUpload component.
 * 
 * @interface ExcelFileUploadProps
 * @property {(file: File) => void} onFileUpload - Callback function when a file is selected
 * @property {boolean} isFileSelected - Indicates if a file is currently selected
 * @property {string} [identifier] - Unique identifier for the file input
 * @property {string} [accept] - File types to accept (e.g., ".xlsx, .xls")
 */
interface ExcelFileUploadProps {
  onFileUpload: (file: File) => void;
  isFileSelected: boolean;
  identifier?: string;
  accept?: string;
}

/**
 * A reusable component for uploading Excel files or other file types.
 * 
 * This component provides a file upload interface that:
 * - Supports Excel and other file types
 * - Uses a hidden file input with a styled button
 * - Handles file selection and upload
 * - Provides visual feedback for file selection
 * 
 * @component
 * @example
 * ```tsx
 * <ExcelFileUpload
 *   onFileUpload={(file) => handleFileUpload(file)}
 *   isFileSelected={hasFile}
 *   identifier="excel-upload"
 *   accept=".xlsx, .xls"
 * />
 * ```
 * 
 * @remarks
 * - Uses Material-UI's Button and Box components
 * - Implements a hidden file input for better UX
 * - Supports custom file type restrictions
 * - Provides German text for upload button
 * - Handles file selection through a callback
 * 
 * @returns {JSX.Element} A file upload component with a styled button
 */
const ExcelFileUpload: FunctionComponent<ExcelFileUploadProps> = (props) => {
  const { onFileUpload, isFileSelected, identifier, accept } = props;

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (file) {
      onFileUpload(file);
    }
  };

  return (
    <Box display="flex"
      flexDirection={{ xs: "column", sm: "row" }}
      alignItems="center"
      justifyContent="space-between"
      gap={1}
      mt={{ xs: 1, sm: 0 }}>
      <Button
        startIcon={<CloudUploadIcon />}
        variant="contained"
        color="secondary"
        size="small"
        sx={{
          fontSize: "1rem",
          textTransform: "none",
        }}
        onClick={handleButtonClick}
      >
        {accept ? "Datei hochladen" : "Excel-Datei hochladen"}
      </Button>

      <input
        ref={fileInputRef}
        key={identifier}
        id={identifier}
        type="file"
        style={{ display: "none" }}
        accept={accept || ".xls, .xlsx"}
        onChange={handleFileChange}
      />

    </Box>
  );
};

export default ExcelFileUpload;
